import React, { useEffect } from 'react';

import { useMutationUpdateSalesChannelConfigHook } from '@fd/customHooks/useMutatationUpdateSalesChannelConfigHook';
import { useQuerySalesChannelConfigHook } from '@fd/customHooks/useQuerySalesChannelConfigHook';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { connect } from 'react-redux';

import { FormSection } from '@fd/ui/molecules';

import { notifyError, NotifyProps, notifySaved } from '../../../../layouts/Notify/actions';
import { getTranslate, Translate } from '../../../../overrides/react-localize-redux';
import { permissionsSelector } from '../../../../selectors/permissions.selector';
import { useTracking } from '../../../../services/amplitude/useTracking';
import { flagService } from '../../../../services/flagService';
import PageLayout from '../../../../ui/Layout';
import PaperContainer from '../../../../ui/Layout/PaperContainer';
import { SalesChannelTypeEnum } from '../../../RMS/utils/salesChannelTypeEnum';
import AssignStoresVisibility from '../../components/AssignStoresVisibility';
import AssignStoresVisibilityOld from '../../components/AssignStoresVisibilityOld';
import { WebToAppRedirectSelectFilter } from '../../components/WebToAppRedirectSelectFilter';

const useStyles = makeStyles((theme: Theme) => ({
  PaperContainer: { paddingBottom: theme.spacing(5) },
  formSection: { marginBottom: theme.spacing(2) },
}));

export const GeneralSiteSettings = (props: MappedState & MappedDispatch) => {
  const {
    addSalesChannelTypeToStoreVisibility,
    appId,
    hasEditSalesChannelsPermission,
    notifySaveError,
    notifySaveSuccess,
    translate,
  } = props;
  const { data, isLoading } = useQuerySalesChannelConfigHook(appId, true);
  const classes = useStyles();
  const { trackEvent } = useTracking();
  const handleConfigUpdateSuccess = () => {
    notifySaveSuccess();
  };

  const handleConfigUpdateFailed = (error) => {
    if (error) {
      notifySaveError(error);
    } else {
      notifySaveError({ message: 'Error_please_try_again_later', translate: true });
    }
  };
  const { mutate } = useMutationUpdateSalesChannelConfigHook(
    appId,
    handleConfigUpdateSuccess,
    handleConfigUpdateFailed
  );

  useEffect(() => {
    trackEvent('portal_SalesChannels_storeVisibility', { action: 'logged_in' });
  }, []);

  return (
    <PageLayout
      strictToParent={true}
      toParent={`/${appId}/sales-channels/website?tab=settings`}
      documentTitle="Site_settings"
      title={<Translate id="Site_settings" />}
      userPermissions={'EditSalesChannels'}
    >
      <PaperContainer fluid className={classes.PaperContainer}>
        <FormSection sectionTitle={translate('Sales_channels_visibility') as string}>
          {addSalesChannelTypeToStoreVisibility ? (
            <AssignStoresVisibility
              description={
                <Translate id="Change_the_visibility_of_the_stores_on_your_website_Only" />
              }
              appId={appId}
              channelId={3}
              translate={translate}
              salesChannelType={SalesChannelTypeEnum.Web}
            />
          ) : (
            <AssignStoresVisibilityOld
              description={
                <Translate id="Change_the_visibility_of_the_stores_on_your_website_Only" />
              }
              appId={appId}
              channelId={3}
              translate={translate}
            />
          )}
        </FormSection>
        <WebToAppRedirectSelectFilter
          isDisabled={!hasEditSalesChannelsPermission}
          isLoading={isLoading}
          onChange={mutate}
          translate={translate}
          value={data?.Data.WebToAppRedirect}
        />
      </PaperContainer>
    </PageLayout>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  notifySaveSuccess: () => dispatch(notifySaved()),
  notifySaveError: (data: NotifyProps) => dispatch(notifyError(data)),
});

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state) => {
  const { currentApp } = state;
  const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['EditSalesChannels']);
  return {
    hasEditSalesChannelsPermission: getPermissionsSelector(state),
    addSalesChannelTypeToStoreVisibility: flagService.isFlagOn(
      state,
      'addSalesChannelTypeToStoreVisibility'
    ),
    appId: currentApp.AppId as string,
    translate: getTranslate(state.locale),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(GeneralSiteSettings);
